import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import { Text, Stack } from '@atlaskit/primitives';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { DynamicResultTable } from '@css-xp/base';
import { CloudProductDetailView, EntitlementStatus, getBilledForColumnData } from '@css-xp/base/entitlement-utils';
import { getFormattedDate } from '@css-xp/entitlements-v2';
import {
  CloudSiteLink,
  emptyStateProp,
  errorStateProp,
  NoData,
  OrgNameLink,
  TableHeading,
  genericEmptyStateProp,
  NextBillDate,
  Usage,
} from '@css-xp/ppc-common';
import { routePaths } from '@css-xp/ppc-constants';
import { getRouteUrl, isFutureDatedInactiveSubscription, isOptedQuantitySourceInherited } from '@css-xp/ppc-utils';
import {
  FetchProductInstancesForEntitlementQueryPpcQuery,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  SearchLinkedEntitlementParams,
  BillingStatus,
} from '@css-xp/graphtypes/src/generated-types-new';
import { AppsCountCell as AppsCount } from '@css-xp/ppc-subscription-table';
import { useFetchProductInstanceForEntitlementPPCQuery } from './use-fetch-product-instances-for-entitlement-ppc-query';
import { AppsCountCell } from './product-instances.styles';

interface ProductInstancesProps {
  entitlementId: string | undefined;
  entitlementType: LinkedEntitlementTypes | undefined;
  productType: LinkedProductTypes | undefined | null;
  transactionAccountId: string;
  cloudProductDetailViewVersion?: number;
  tableHeading?: string;
  searchLinkedEntitlementParams?: Partial<SearchLinkedEntitlementParams>;
  isFutureDatedEntitlementEnabled?: boolean;
  isDesignIteration1Enabled?: boolean;
}

const getDataForCurrentPage = (
  currentPage: number,
  pageSize: number,
  data: FetchProductInstancesForEntitlementQueryPpcQuery['entitlementDetails']['linkedEntitlements']
) => {
  if (!data || data.length === 0) return [];
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data?.slice(start, end);
};

export const ProductInstances = ({
  entitlementId,
  entitlementType,
  productType,
  transactionAccountId,
  cloudProductDetailViewVersion,
  tableHeading,
  searchLinkedEntitlementParams,
  isFutureDatedEntitlementEnabled = false,
  isDesignIteration1Enabled = false,
}: ProductInstancesProps) => {
  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });
  const [tableViewData, setTableViewData] = useState<{
    data: FetchProductInstancesForEntitlementQueryPpcQuery['entitlementDetails']['linkedEntitlements'];
    currentPage: number;
    rowsPerPage: number;
  }>({
    data: [],
    currentPage: 1,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  });

  const { loading, data, error } = useFetchProductInstanceForEntitlementPPCQuery({
    entitlementId: entitlementId || '',
    entitlementType,
    productType,
    transactionAccountId,
    searchLinkedEntitlementParams,
  });

  const linkedEntitlements: FetchProductInstancesForEntitlementQueryPpcQuery['entitlementDetails']['linkedEntitlements'] = useMemo(
    () => data?.entitlementDetails?.linkedEntitlements || [],
    [data?.entitlementDetails?.linkedEntitlements]
  );

  useEffect(
    () =>
      setTableViewData({
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        currentPage: 1,
        data: getDataForCurrentPage(1, DEFAULT_ROWS_PER_PAGE, linkedEntitlements),
      }),
    [linkedEntitlements]
  );

  const handleRowSizeChange = useCallback(
    (rowSize: number) => {
      setTableViewData({
        currentPage: 1,
        rowsPerPage: rowSize,
        data: getDataForCurrentPage(1, rowSize, linkedEntitlements),
      });
    },
    [linkedEntitlements]
  );

  const handlePageChange = useCallback(
    (pageNo: number) => {
      setTableViewData({
        ...tableViewData,
        currentPage: pageNo,
        data: getDataForCurrentPage(pageNo, tableViewData.rowsPerPage, linkedEntitlements),
      });
    },
    [tableViewData, linkedEntitlements]
  );

  const renderEntitlementDetails = (linkedEntitlement: any) => (
    <CloudProductDetailView
      productId={linkedEntitlement?.productId as string}
      entitlementNumber={linkedEntitlement?.entitlementNumber || ''}
      edition={linkedEntitlement?.edition}
      provisioningStatus={linkedEntitlement?.provisioningStatus}
      productName={linkedEntitlement?.productName}
      cloudSiteId={linkedEntitlement?.cloudSiteId}
      trial={linkedEntitlement?.trial ?? false}
      offeringPricingType={linkedEntitlement?.offering?.pricingType as string}
      isApp={linkedEntitlement?.isApp ?? false}
      isSandbox={linkedEntitlement?.isSandbox ?? false}
      productLink={linkedEntitlement?.entitlementNumber ?? ''}
      version={cloudProductDetailViewVersion}
      isAddOn={!!linkedEntitlement?.isAddOn}
      shouldHideEntitlementDetails={isDesignIteration1Enabled}
    />
  );

  const renderEntitlementStatus = (linkedEntitlement: any) => {
    const isFutureDatedInActiveEntitlement =
      isFutureDatedEntitlementEnabled &&
      isFutureDatedInactiveSubscription(
        !!linkedEntitlement?.isFutureDatedSubscription,
        linkedEntitlement?.billingStatus as BillingStatus
      );

    return (
      <EntitlementStatus
        billingStatus={linkedEntitlement?.billingStatus}
        provisioningStatus={linkedEntitlement?.provisioningStatus}
        scheduledChanges={linkedEntitlement?.scheduledChanges}
        isFutureDatedInactiveEntitlement={isFutureDatedInActiveEntitlement}
        hideInactiveStatus={isFutureDatedInActiveEntitlement}
      />
    );
  };

  const renderCloudsiteLink = (linkedEntitlement: any) => (
    <CloudSiteLink
      siteName={linkedEntitlement?.cloudSiteHostname}
      txaId={transactionAccountId}
      cloudSiteId={linkedEntitlement?.cloudSiteId}
    />
  );

  const getHead = useCallback(() => {
    if (isDesignIteration1Enabled) {
      return {
        cells: [
          {
            content: 'Subscription',
            shouldTruncate: true,
            width: 15,
          },
          {
            content: 'Status',
            width: 10,
          },
          {
            content: 'Plan',
            width: 15,
          },
          {
            content: 'Apps',
            width: 10,
          },
          {
            content: 'Site',
            width: 15,
          },
          {
            content: 'Quantity',
            width: 10,
          },
          {
            content: 'Renew date',
            width: 10,
          },
        ],
      };
    }
    return {
      cells: [
        {
          content: 'Subscription',
          shouldTruncate: true,
          width: 15,
        },
        {
          content: 'Status',
          width: 10,
        },
        {
          content: 'Apps',
          width: 10,
        },
        {
          content: 'Org name',
          width: 15,
        },
        {
          content: 'Site',
          width: 15,
        },
        {
          content: 'Usage',
          width: 10,
        },
        {
          content: 'Billing cycle',
          width: 10,
        },
        {
          content: 'Created',
          width: 10,
        },
        {
          content: 'Entitlement number',
          width: 15,
        },
      ],
    };
  }, [isDesignIteration1Enabled]);

  const getRows = useCallback(() => {
    return tableViewData.data?.map((linkedEntitlement) => {
      const appsCount = linkedEntitlement?.linkedAppsId?.length ?? 0;
      const activeUsers = linkedEntitlement?.activeUsers ?? 0;

      if (isDesignIteration1Enabled) {
        return {
          key: linkedEntitlement?.entitlementId,
          cells: [
            {
              content: renderEntitlementDetails(linkedEntitlement),
            },
            {
              content: renderEntitlementStatus(linkedEntitlement),
            },
            {
              content: (
                <Stack>
                  <Text>{linkedEntitlement?.edition}</Text>
                  <Text size="small" color="color.text.subtlest">
                    {linkedEntitlement?.billingFrequency ? capitalize(linkedEntitlement.billingFrequency) : <NoData />}
                  </Text>
                </Stack>
              ),
            },
            {
              content: (
                <AppsCount
                  version={2}
                  subscription={{
                    id: linkedEntitlement?.entitlementId ?? '',
                    linkedAppsId: linkedEntitlement?.linkedAppsId ?? [],
                  }}
                  baseRoute={subscriptionsRoutePPC}
                />
              ),
            },
            {
              content: renderCloudsiteLink(linkedEntitlement),
            },
            {
              content: (
                <Usage
                  activeUsers={activeUsers}
                  chargeElement={linkedEntitlement?.chargeElement}
                  userTier={linkedEntitlement?.userTier}
                  edition={linkedEntitlement?.edition as string}
                />
              ),
            },
            {
              content: <NextBillDate entitlement={linkedEntitlement as any} hideChangeBillDateButton />,
            },
          ],
        };
      }

      return {
        key: linkedEntitlement?.entitlementId,
        cells: [
          {
            content: renderEntitlementDetails(linkedEntitlement),
          },
          {
            content: renderEntitlementStatus(linkedEntitlement),
          },
          {
            content: (
              <Link
                to={
                  linkedEntitlement?.entitlementId
                    ? `${subscriptionsRoutePPC}${linkedEntitlement?.entitlementId}#apps`
                    : '#'
                }
              >
                <AppsCountCell isAppPresent={appsCount !== 0}> {appsCount}</AppsCountCell>
              </Link>
            ),
          },
          {
            content: (
              <OrgNameLink
                orgName={linkedEntitlement?.provisionedResourceDetails?.organisationName}
                orgId={linkedEntitlement?.provisionedResourceDetails?.organisationId}
                txaId={transactionAccountId}
              />
            ),
          },
          {
            content: renderCloudsiteLink(linkedEntitlement),
          },
          {
            content: (
              <>
                <div>
                  {activeUsers}{' '}
                  {capitalize(
                    getBilledForColumnData(linkedEntitlement?.productId, linkedEntitlement?.edition, activeUsers === 1)
                  )}
                </div>
                {!isOptedQuantitySourceInherited(linkedEntitlement?.optedQuantitySource) &&
                  linkedEntitlement?.userTier && (
                    <div data-testid="linked-ent-user-tier">User tier - {linkedEntitlement?.userTier}</div>
                  )}
              </>
            ),
          },
          {
            content: linkedEntitlement?.billingFrequency ? capitalize(linkedEntitlement.billingFrequency) : <NoData />,
          },
          {
            content: linkedEntitlement?.createdDate ? getFormattedDate(linkedEntitlement.createdDate) : <NoData />,
          },
          {
            content: linkedEntitlement?.entitlementNumber,
          },
        ],
      };
    });
  }, [
    subscriptionsRoutePPC,
    tableViewData.data,
    transactionAccountId,
    isDesignIteration1Enabled,
    cloudProductDetailViewVersion,
    isFutureDatedEntitlementEnabled,
  ]);

  return (
    <DynamicResultTable
      isLoading={loading}
      testId="product-instances-table"
      beforeTable={isDesignIteration1Enabled ? undefined : <TableHeading>{tableHeading}</TableHeading>}
      head={getHead()}
      rows={getRows()}
      emptyStateProps={isDesignIteration1Enabled ? genericEmptyStateProp : emptyStateProp}
      totalCount={linkedEntitlements.length}
      currentPage={tableViewData.currentPage}
      rowsPerPage={tableViewData.rowsPerPage}
      onPageChange={handlePageChange}
      isPaginationEnabled={linkedEntitlements.length > DEFAULT_ROWS_PER_PAGE}
      onRowChange={handleRowSizeChange}
      errorStateProps={errorStateProp}
      error={error?.message}
      hideHeadOnEmptyState={isDesignIteration1Enabled}
    />
  );
};
